import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from '@/lib/gsap';
import { prefersReducedMotion } from '@/lib/lenis';
import { cn } from '@/lib/cn';

type CursorMode = 'default' | 'hover' | 'view' | 'click' | 'text' | 'hidden';

/**
 * CustomCursor — desktop pointer replacement.
 *
 * - Default: 12px magenta dot (bright white outline + glow) with an always-visible
 *   trailing ring — kept high-contrast so it stays locatable on the dark theme
 * - Hover (interactive elements): 32px ring outline, mix-blend-mode: difference
 * - View ("VIEW" text on product images)
 * - Click: brief scale-down flash
 * - Hidden: completely inactive (opacity: 0) on form inputs, textareas, selects, and iframes
 *
 * Hidden on touch devices (`@media pointer: coarse`) and reduced-motion.
 *
 * Detects hover state via document-wide pointer events listening for elements
 * with [data-cursor="view"] / [data-cursor="hover"] attributes (or natural
 * interactive tags: a, button, input, label, [role="button"]).
 *
 * Uses GSAP `quickTo` for sub-frame precision — far smoother than rAF + setState.
 */
export function CustomCursor() {
  const dotRef = useRef<HTMLDivElement | null>(null);
  const ringRef = useRef<HTMLDivElement | null>(null);
  const [mode, setMode] = useState<CursorMode>('default');
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (prefersReducedMotion()) return;
    const isCoarse = window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) return;
    setEnabled(true);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    document.documentElement.classList.add('cursor-none-q');

    // GSAP quickTo for buttery 60fps cursor — different easing for ring/dot
    // creates the trailing-ring depth illusion.
    const dotX = gsap.quickTo(dot, 'x', { duration: 0, ease: 'none' });
    const dotY = gsap.quickTo(dot, 'y', { duration: 0, ease: 'none' });
    const ringX = gsap.quickTo(ring, 'x', { duration: 0.35, ease: 'expo.out' });
    const ringY = gsap.quickTo(ring, 'y', { duration: 0.35, ease: 'expo.out' });

    const onMove = (e: PointerEvent) => {
      dotX(e.clientX);
      dotY(e.clientY);
      ringX(e.clientX);
      ringY(e.clientY);
    };

    const onDown = () => setMode('click');
    const onUp = () => setMode((m) => (m === 'click' ? 'default' : m));

    const onOver = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      // Restore native cursor on text fields, selects, and iframes for peak usability
      const isTextInput = target.closest('input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea');
      const isSelectOrIframe = target.closest('select, iframe');

      if (isTextInput || isSelectOrIframe) {
        setMode('hidden');
        return;
      }

      const cursorAttr =
        target.closest('[data-cursor="view"]') ? 'view' :
        target.closest('[data-cursor="hover"]') ? 'hover' :
        target.closest('[data-cursor="text"]') ? 'text' :
        target.closest('a, button, [role="button"], input, label, select, textarea, [data-magnetic]') ? 'hover' :
        'default';
      setMode(cursorAttr as CursorMode);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    document.addEventListener('mouseover', onOver);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      document.removeEventListener('mouseover', onOver);
      document.documentElement.classList.remove('cursor-none-q');
    };
  }, [enabled]);

  if (!enabled) return null;

  return createPortal(
    <>
      {/* Outer ring — animates size/opacity with mode */}
      <div
        ref={ringRef}
        aria-hidden
        className={cn(
          'pointer-events-none fixed left-0 top-0 z-[999999]',
          '-ml-[var(--ring-size)]/2 -mt-[var(--ring-size)]/2',
          'flex items-center justify-center rounded-full border border-white/40',
          'transition-[width,height,opacity,background-color,border-color] duration-300 ease-[var(--ease-out-expo)]',
          'mix-blend-difference'
        )}
        style={{
          width: ringSize(mode),
          height: ringSize(mode),
          marginLeft: `calc(${ringSize(mode)} / -2)`,
          marginTop:  `calc(${ringSize(mode)} / -2)`,
          opacity: mode === 'hidden' ? 0 : mode === 'default' ? 0.75 : 1,
          background:
            mode === 'view'  ? 'rgba(212, 0, 106, 0.95)' :
            mode === 'click' ? 'rgba(255, 255, 255, 0.15)' :
            'transparent',
          borderColor:
            mode === 'hidden' ? 'transparent' :
            mode === 'view'  ? 'transparent' :
            mode === 'hover' ? 'rgba(255, 255, 255, 0.9)' :
            'rgba(255, 255, 255, 0.7)',
        }}
      >
        {mode === 'view' && (
          <span className="text-[10px] font-[family-name:var(--font-label)] uppercase tracking-[0.2em] text-white">
            View
          </span>
        )}
      </div>

      {/* Inner dot */}
      <div
        ref={dotRef}
        aria-hidden
        className="pointer-events-none fixed left-0 top-0 z-[999999] -ml-[6px] -mt-[6px] h-3 w-3 rounded-full bg-[color:var(--color-brand-magenta)]"
        style={{
          opacity: (mode === 'view' || mode === 'hidden' || mode === 'text') ? 0 : 1,
          transform: mode === 'click' ? 'scale(0.6)' : 'scale(1)',
          transition: 'opacity 200ms ease, transform 150ms ease',
          // Bright white ring + magenta glow keeps the click point visible on any
          // background (the dark theme was swallowing the old 8px solid dot).
          boxShadow: '0 0 0 2px rgba(255,255,255,0.95), 0 0 12px 3px rgba(212,0,106,0.9)',
        }}
      />
    </>,
    document.body
  );
}

function ringSize(mode: CursorMode): string {
  switch (mode) {
    case 'hidden': return '0px';
    case 'view':  return '72px';
    case 'hover': return '38px';
    case 'click': return '24px';
    case 'text':  return '4px';
    case 'default':
    default:      return '32px';
  }
}
